import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { downloadExam } from '../utils/api';
import {
  Box,
  Typography,
  Button,
  TextField,
  LinearProgress,
  Alert,
  Chip,
  Grid,
  Divider,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  useMediaQuery,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Fade
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { processLatexString } from '../components/LatexRenderer';
import ValidationResult from '../components/review/ValidationResult';
import ActionButtons from '../components/review/ActionButtons';
import QuestionPreview from '../components/review/QuestionPreview';
import AnswerPreview from '../components/review/AnswerPreview';
import DragDropLatexEditor from '../components/review/DragDropLatexEditor';
import { STYLES } from '../components/review/constants';
import useTemplatedReview from '../hooks/useTemplatedReview';
import useBackgroundCompilation from '../hooks/useBackgroundCompilation';
import { reviewThemeExtensions } from '../theme/reviewTheme';
import NotificationSystem from '../components/NotificationSystem';
import InAppNotification from '../components/InAppNotification';

const CompleteReview = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Responsive design - hide sidebar on screens smaller than breakpoint
  const isLargeScreen = useMediaQuery(theme.breakpoints.up(STYLES.breakpoints.sidebarHide));

  // State management
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [compilationErrors, setCompilationErrors] = useState([]);
  const [error, setError] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Use the templated review hook for editing logic
  const { 
    handleTemplatedEdit: handleTemplatedEditHook,
    getEditedValues: getEditedValuesHook,
    clearEditedValues
  } = useTemplatedReview();

  // Use the background compilation hook
  const {
    notifications,
    failedSegments,
    removeNotification,
    lockQuestionInBackground
  } = useBackgroundCompilation();

  // Topic override state
  const [topicOverrideOpen, setTopicOverrideOpen] = useState(false);
  const [overrideTopic, setOverrideTopic] = useState('');
  const [topics, setTopics] = useState([]);
  
  // Answer override state - removed (unused)
  
  // MCQ answer selection state
  const [selectedMcqAnswers, setSelectedMcqAnswers] = useState({});
  
  // Image upload state
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageMap, setImageMap] = useState({});
  
  // Original content visibility
  const [showOriginalContent, setShowOriginalContent] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // Helper to get the modifier key name based on OS
  const getModifierKey = () => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? '⌥' : 'Alt';  // ⌥ is the Option/Alt symbol on Mac
  };
  
  // Helper to show notifications
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  // Fetch all questions for the document
  useEffect(() => {
    fetchQuestions();
    fetchTopics();
    fetchDocumentImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // Keyboard navigation and shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isAltPressed = e.altKey;
      
      // Alt-based shortcuts (work even in inputs)
      if (isAltPressed) {
        // On Mac, Alt/Option + key sometimes produces special characters
        // So we'll check both the key and keyCode
        const key = e.key.toLowerCase();
        const keyCode = e.keyCode;
        
        // Handle by keyCode for more reliable detection
        
        // Alt+N (keyCode 78)
        if (key === 'n' || keyCode === 78) {
          e.preventDefault();
          e.stopPropagation();
          if (handleNextUnverified) handleNextUnverified();
        }
        // Alt+T (keyCode 84)
        else if (key === 't' || keyCode === 84) {
          e.preventDefault();
          e.stopPropagation();
          if (!currentQuestion.verification_status?.is_fully_verified) {
            handleTopicOverrideOpen();
          }
        }
        // Alt+Q (keyCode 81)
        else if (key === 'q' || keyCode === 81) {
          e.preventDefault();
          e.stopPropagation();
          // Focus on question editor - find the textarea in the Question LaTeX section
          const questionSection = Array.from(document.querySelectorAll('h6')).find(h => h.textContent.includes('Question LaTeX'));
          if (questionSection) {
            const questionPanel = questionSection.closest('.MuiPaper-root');
            if (questionPanel) {
              const questionTextarea = questionPanel.querySelector('textarea');
              if (questionTextarea) {
                questionTextarea.focus();
                questionTextarea.select();
              }
            }
          }
        }
        // Alt+A (keyCode 65)
        else if (key === 'a' || keyCode === 65) {
          e.preventDefault();
          e.stopPropagation();
          // Focus on answer editor - find the textarea in the Answer LaTeX section
          const answerSection = Array.from(document.querySelectorAll('h6')).find(h => h.textContent.includes('Answer LaTeX'));
          if (answerSection) {
            const answerPanel = answerSection.closest('.MuiPaper-root');
            if (answerPanel) {
              // First try to find a textarea (for extended questions)
              const answerTextarea = answerPanel.querySelector('textarea');
              if (answerTextarea) {
                answerTextarea.focus();
                answerTextarea.select();
              } else {
                // If no textarea, look for input fields (for MCQ options)
                const firstInput = answerPanel.querySelector('input[type="text"]');
                if (firstInput) {
                  firstInput.focus();
                  firstInput.select();
                }
              }
            }
          }
        }
        // Alt+L (keyCode 76)
        else if (key === 'l' || keyCode === 76) {
          e.preventDefault();
          e.stopPropagation();
          if (!currentQuestion.verification_status?.is_fully_verified && !needsMcqAnswerSelection()) {
            handleLockIntoDatabase();
          }
        }
        
        return;
      }
      
      // Don't handle arrow keys if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Arrow key navigation
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (handlePrevious) handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (handleNext) handleNext();
          break;
        default:
          // No action needed for other keys
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });


  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/review/complete/${documentId}`);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      // Always set empty array on error, no mock data
      setQuestions([]);
      setError(error.response?.data?.detail || 'Failed to load questions. Please check the API connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await api.get('/topics/paths');
      setTopics(response.data || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      setTopics([]);
    }
  };

  const fetchDocumentImages = async () => {
    try {
      const response = await api.get(`/database/documents/${documentId}/images`);
      const images = response.data || [];
      // Create a map of filename to image ID for quick lookup
      const map = {};
      images.forEach(img => {
        // Add the exact filename
        map[img.filename] = img.id;
        
        // Also add the filename without extension for flexible matching
        const baseFilename = img.filename.replace(/\.[^/.]+$/, '');
        map[baseFilename] = img.id;
      });
      setImageMap(map);
    } catch (error) {
      console.error('Error fetching document images:', error);
    }
  };

  // Handle exam generation
  const handleGenerateExam = async () => {
    try {
      setGeneratingExam(true);
      setExamGenerationStatus('Preparing document exam generation...');
      
      // Calculate how many verified questions we have
      const verifiedQuestions = questions.filter(q => q.verification_status?.is_fully_verified);
      
      if (verifiedQuestions.length === 0) {
        showNotification('No verified questions available. Please review and lock questions first.', 'warning');
        setGeneratingExam(false);
        return;
      }
      
      setExamGenerationStatus(`Generating exam for document ${documentId}...`);
      
      // Call the document-specific exam generation endpoint
      const response = await api.post('/generate/exam/document', {
        document_id: parseInt(documentId),
        skip_pdf: false
      });
      
      const taskId = response.data.task_id;
      setExamGenerationTaskId(taskId);
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get(`/generate/exam/${taskId}`);
          const status = statusResponse.data;
          
          setExamGenerationStatus(status.message || 'Generating exam...');
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setGeneratingExam(false);
            
            // Download the exam using the same method as GenerateExam.js
            try {
              const downloadResponse = await downloadExam(taskId);
              
              const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
              const link = document.createElement('a');
              link.href = url;
              
              const contentDisposition = downloadResponse.headers['content-disposition'];
              let filename = 'exam';
              
              if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                  filename = filenameMatch[1];
                }
              } else {
                const ext = downloadResponse.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
                filename = `exam_document_${documentId}_${taskId.substring(0, 8)}${ext}`;
              }
              
              if (!/\.(pdf|tex)$/i.test(filename)) {
                const ext = downloadResponse.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
                filename += ext;
              }
              
              link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
              
              window.URL.revokeObjectURL(url);
              document.body.removeChild(link);
              
              showNotification('Exam generated and downloaded successfully!', 'success');
              setExamGenerationStatus(null);
              // Don't hide the generate exam section anymore - keep it visible for re-download
              setExamGenerationTaskId(taskId); // Keep the task ID for re-download
            } catch (downloadError) {
              console.error('Failed to download exam:', downloadError);
              showNotification('Exam generated but download failed. You can try downloading again.', 'warning');
            }
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setGeneratingExam(false);
            showNotification(`Exam generation failed: ${status.message}`, 'error');
            setExamGenerationStatus(null);
            setExamGenerationTaskId(null);
          }
        } catch (error) {
          console.error('Error polling exam status:', error);
          clearInterval(pollInterval);
          setGeneratingExam(false);
          showNotification('Failed to check exam generation status', 'error');
          setExamGenerationStatus(null);
          setExamGenerationTaskId(null);
        }
      }, 2000); // Poll every 2 seconds
      
    } catch (error) {
      console.error('Error generating exam:', error);
      setGeneratingExam(false);
      
      // Check if it's an authentication/permission error
      if (error.response?.status === 403 || error.response?.status === 401) {
        showNotification('Exam generation requires student privileges. As a tutor/admin, you have successfully verified all questions for student use.', 'warning');
      } else {
        showNotification('Failed to generate exam: ' + (error.response?.data?.detail || 'Unknown error'), 'error');
      }
      
      setExamGenerationStatus(null);
      setExamGenerationTaskId(null);
    }
  };
  
  // Handle re-download of exam
  const handleRedownloadExam = async () => {
    if (!examGenerationTaskId) {
      showNotification('No exam available for download', 'warning');
      return;
    }
    
    try {
      const downloadResponse = await downloadExam(examGenerationTaskId);
      
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = downloadResponse.headers['content-disposition'];
      let filename = 'exam';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      } else {
        const ext = downloadResponse.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
        filename = `exam_document_${documentId}_${examGenerationTaskId.substring(0, 8)}${ext}`;
      }
      
      if (!/\.(pdf|tex)$/i.test(filename)) {
        const ext = downloadResponse.headers['content-type'] === 'application/pdf' ? '.pdf' : '.tex';
        filename += ext;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      showNotification('Exam downloaded successfully!', 'success');
    } catch (error) {
      console.error('Failed to re-download exam:', error);
      showNotification('Failed to download exam. The exam may have expired.', 'error');
    }
  };

  const currentQuestion = questions[currentIndex] || {};
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  
  // Debug logging
  if (currentQuestion.templated) {
    console.log('Current question:', {
      index: currentIndex,
      segmentId: currentQuestion.segment_id,
      questionNumber: currentQuestion.question_number,
      templatedType: currentQuestion.templated.type,
      hasParts: currentQuestion.templated.has_parts,
      parts: currentQuestion.templated.parts,
      content: currentQuestion.content,
      solution: currentQuestion.templated.solution,
      answer: currentQuestion.answer,
      templated: currentQuestion.templated
    });
  }

  // Handle navigation with animation
  const transitionToQuestion = (newIndex) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setValidationResult(null);
      setCompilationErrors([]);
      clearEditedValues();
      setShowOriginalContent(false); // Reset original content visibility
      
      // Check if we should show generate exam button
      const isLastQuestion = newIndex === questions.length - 1;
      const allQuestionsVerified = questions.every(q => !q.templated || q.verification_status?.is_fully_verified);
      const hasVerifiedQuestions = questions.some(q => q.verification_status?.is_fully_verified);
      
      // Note: Generate exam button is now always visible in ActionButtons
      
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 200);
  };

  const handlePrevious = () => {
    // Find previous processed question
    let prevIndex = currentIndex - 1;
    while (prevIndex >= 0 && !questions[prevIndex].templated) {
      prevIndex--;
    }
    
    if (prevIndex >= 0) {
      transitionToQuestion(prevIndex);
    }
  };

  const handleNext = () => {
    // Find next processed question
    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length && !questions[nextIndex].templated) {
      nextIndex++;
    }
    
    if (nextIndex < questions.length) {
      transitionToQuestion(nextIndex);
    }
  };

  // Wrapper functions to adapt the hook to current usage
  const handleTemplatedEdit = (field, value) => {
    const templated = currentQuestion?.templated;
    handleTemplatedEditHook(templated, field, value);
  };

  const getEditedValues = () => {
    const templated = currentQuestion?.templated;
    return getEditedValuesHook(templated);
  };

  // Compile test for specific section
  const handleValidateSection = async (section) => {
    const templated = currentQuestion.templated;
    if (!templated) return;

    setValidating(true);
    setValidationResult(null);

    try {
      const edited = getEditedValues();
      
      // Prepare the edited content based on template type
      let editedContent = {};
      
      if (templated.type === 'mcq') {
        editedContent = {
          question_content: edited.question_content || templated.question_content,
          option_a: edited.option_a || templated.option_a,
          option_b: edited.option_b || templated.option_b,
          option_c: edited.option_c || templated.option_c,
          option_d: edited.option_d || templated.option_d
        };
      } else {
        editedContent = {
          question_content: edited.question_content || templated.question_content,
          solution: edited.solution || templated.solution
        };
      }

      // Call the compile-test endpoint
      const response = await api.post('/review/templated/compile-test', {
        content_type: templated.type,
        content_id: currentQuestion.segment_id,
        edited_content: editedContent
      });
      
      const result = response.data;
      
      // Convert compile result to validation result format
      setValidationResult({
        is_valid: result.success,
        message: result.message,
        error_message: result.errors && result.errors.length > 0 ? result.errors.join('; ') : null
      });
      
      // Store detailed errors for display
      if (!result.success && result.errors && result.errors.length > 0) {
        setCompilationErrors(result.errors);
      } else {
        setCompilationErrors([]);
      }
    } catch (error) {
      console.error('Compilation error:', error);
      setValidationResult({
        is_valid: false,
        message: 'Compilation test failed',
        error_message: error.response?.data?.detail || 'Unknown error'
      });
    } finally {
      setValidating(false);
    }
  };

  // State for exam generation
  const [generatingExam, setGeneratingExam] = useState(false);
  const [examGenerationTaskId, setExamGenerationTaskId] = useState(null);
  const [examGenerationStatus, setExamGenerationStatus] = useState(null);

  // Lock into database - new implementation with background processing
  const handleLockIntoDatabase = async () => {
    const templated = currentQuestion.templated;
    
    // Check if question is processed
    if (!templated) {
      setValidationResult({
        is_valid: false,
        message: 'Cannot lock: Question not processed',
        error_message: 'This question has not been processed by AI yet. Please wait for processing to complete.'
      });
      return;
    }

    // First, check if MCQ needs answer selection
    if (templated.type === 'mcq' && currentQuestion.answer?.answer_type === 'CANNOT_ANSWER') {
      const selectedAnswer = getSelectedMcqAnswer();
      if (!selectedAnswer) {
        setValidationResult({
          is_valid: false,
          message: 'Cannot lock: MCQ answer required',
          error_message: 'AI could not determine the answer. Please select the correct option before locking.'
        });
        return;
      }
    }

    // Prepare review data
    const edited = getEditedValues();
    const reviewData = {
      topic_review: null,
      answer_review: null,
      template_review: null
    };

    // Topic review - check if changed from AI
    if (currentQuestion.topic) {
      const userTopic = currentQuestion.topic.user_topic_path;
      const aiTopic = currentQuestion.topic.ai_topic_path;
      
      // Only include if user has made a change or if not verified
      if ((userTopic && userTopic !== aiTopic) || !currentQuestion.verification_status?.is_topic_verified) {
        reviewData.topic_review = {
          topic_path: userTopic || null // null means approve AI topic
        };
      }
    }

    // Answer review - ALWAYS send current content from edit boxes
    if (templated.type === 'mcq') {
      const selectedAnswer = getSelectedMcqAnswer();
      const aiAnswer = currentQuestion.answer?.ai_answer || '';
      
      // Always include answer review with current selection
      reviewData.answer_review = {
        answer_text: selectedAnswer !== aiAnswer ? selectedAnswer : null
      };
    } else {
      // For extended questions, ALWAYS use what's currently in the edit box
      const currentSolution = edited.solution || templated.solution || '';
      const aiSolution = templated.solution || '';
      
      // Always include answer review - send current content if different from AI
      reviewData.answer_review = {
        answer_text: currentSolution !== aiSolution ? currentSolution : null
      };
    }

    // Template review - prepare current values
    const currentValues = {};
    
    if (templated.type === 'mcq') {
      const currentQuestionContent = edited.question_content || templated.question_content;
      const currentOptionA = edited.option_a || templated.option_a;
      const currentOptionB = edited.option_b || templated.option_b;
      const currentOptionC = edited.option_c || templated.option_c;
      const currentOptionD = edited.option_d || templated.option_d;
      
      // Only include if different from AI-generated
      if (currentQuestionContent !== templated.question_content) {
        currentValues.human_question_content = currentQuestionContent;
      }
      if (currentOptionA !== templated.option_a) {
        currentValues.human_option_a = currentOptionA;
      }
      if (currentOptionB !== templated.option_b) {
        currentValues.human_option_b = currentOptionB;
      }
      if (currentOptionC !== templated.option_c) {
        currentValues.human_option_c = currentOptionC;
      }
      if (currentOptionD !== templated.option_d) {
        currentValues.human_option_d = currentOptionD;
      }
    } else {
      const currentQuestionContent = edited.question_content || templated.question_content;
      const currentSolution = edited.solution || templated.solution;
      
      // Only include if different from AI-generated
      if (currentQuestionContent !== templated.question_content) {
        currentValues.human_main_content = currentQuestionContent;
      }
      // For solution/answer, include in template review if different
      if (currentSolution !== templated.solution) {
        currentValues.human_solution = currentSolution;
      }
      
      // Handle parts if present
      if (templated.has_parts && templated.parts) {
        const partEdits = {};
        templated.parts.forEach(part => {
          const contentKey = `part_${part.id}_content`;
          const answerKey = `part_${part.id}_answer`;
          
          const currentPartContent = edited[contentKey] || part.part_content;
          const currentPartAnswer = edited[answerKey] || part.part_answer;
          
          // Check if either content or answer was changed for this part
          if (currentPartContent !== part.part_content || currentPartAnswer !== part.part_answer) {
            partEdits[part.id] = {
              human_part_content: currentPartContent !== part.part_content ? currentPartContent : undefined,
              human_part_answer: currentPartAnswer !== part.part_answer ? currentPartAnswer : undefined
            };
          }
        });
        
        // Only include parts if there are edits
        if (Object.keys(partEdits).length > 0) {
          currentValues.parts = partEdits;
        }
      }
    }
    
    // Always send template review, even if empty (meaning AI content approved)
    reviewData.template_review = {
      template_data: currentValues
    };

    // Enhanced debug logging
    console.log('=== LOCK INTO DATABASE DEBUG ===');
    console.log('Content ID:', currentQuestion.segment_id);
    console.log('Question Type:', templated.type);
    console.log('Edited values from form:', edited);
    console.log('AI Solution:', templated.solution);
    console.log('Current Solution in edit box:', edited.solution || 'EMPTY');
    console.log('Answer Review being sent:', reviewData.answer_review);
    console.log('Template Review being sent:', reviewData.template_review);
    console.log('Full review data:', JSON.stringify(reviewData, null, 2));
    console.log('================================');

    // Clear any existing validation messages
    setValidationResult(null);
    setCompilationErrors([]);

    // Save current index for the background job
    const questionIndex = currentIndex;
    
    // Check if this is the last question
    const isLastQuestion = currentIndex === questions.length - 1;
    const isLastUnverified = !questions.slice(currentIndex + 1).some(q => !q.verification_status?.is_fully_verified && !!q.templated);
    
    // Start background compilation
    lockQuestionInBackground(
      currentQuestion,
      reviewData,
      // onSuccess callback
      (lockData) => {
        // Update local state with verification statuses
        setQuestions(prev => prev.map((q, idx) => 
          idx === questionIndex 
            ? { 
                ...q, 
                verification_status: {
                  is_topic_verified: lockData.is_topic_verified,
                  is_answer_verified: lockData.is_answer_verified,
                  is_template_verified: lockData.is_template_verified,
                  is_fully_verified: lockData.is_fully_verified
                },
                topic: { ...q.topic, is_verified: lockData.is_topic_verified },
                answer: { ...q.answer, is_verified_human: lockData.is_answer_verified },
                templated: { ...q.templated, is_verified_human: lockData.is_template_verified }
              }
            : q
        ));
        
        // If this is the last question or last unverified, show notification
        if (isLastQuestion || isLastUnverified) {
          showNotification('All questions reviewed! You can now generate an exam from this document.', 'success');
        }
      },
      // onError callback
      (errorMessage, compilationErrors) => {
        // Error is already shown in notification
        console.error('Background lock failed:', errorMessage);
      }
    );

    // Only transition to next question if not the last one
    if (!isLastQuestion && !isLastUnverified) {
      // Add a small delay before transitioning to show the processing has started
      setTimeout(() => {
        // Move to next unverified question with animation (skip unprocessed)
        const nextUnverifiedIndex = questions.findIndex((q, idx) => 
          idx > currentIndex && !q.verification_status?.is_fully_verified && !!q.templated
        );
        
        if (nextUnverifiedIndex !== -1) {
          transitionToQuestion(nextUnverifiedIndex);
        } else {
          // No more unverified questions, try next question
          if (currentIndex < questions.length - 1) {
            transitionToQuestion(currentIndex + 1);
          }
        }
      }, 300); // 300ms delay to show processing has started
    }
  };

  // Handle unlock from database
  const handleUnlockFromDatabase = async () => {
    const segment_id = currentQuestion.segment_id;
    if (!segment_id) return;

    setValidationResult(null);
    
    try {
      const response = await api.post(`/review/templated/unlock-from-database/${segment_id}`);
      const data = response.data;

      if (data.success) {
        // Update local state to reflect unlocked status
        setQuestions(prev => prev.map((q, idx) => 
          idx === currentIndex 
            ? { 
                ...q, 
                verification_status: {
                  is_topic_verified: false,
                  is_answer_verified: false,
                  is_template_verified: false,
                  is_fully_verified: false
                },
                topic: { ...q.topic, is_verified: false },
                answer: { ...q.answer, is_verified_human: false },
                templated: { ...q.templated, is_verified_human: false }
              }
            : q
        ));

        // Show success message
        setValidationResult({
          is_valid: true,
          message: 'Question unlocked for editing!'
        });
      }
    } catch (error) {
      console.error('Unlock error:', error);
      setValidationResult({
        is_valid: false,
        message: 'Failed to unlock question',
        error_message: error.response?.data?.detail || 'Unknown error'
      });
    }
  };

  // Validate LaTeX content
  const handleValidate = async () => {
    const templated = currentQuestion.templated;
    if (!templated) return;

    setValidating(true);
    setValidationResult(null);

    try {
      const edited = getEditedValues();
      const content = templated.type === 'mcq' 
        ? [
            edited.question_content || templated.question_content,
            edited.option_a || templated.option_a,
            edited.option_b || templated.option_b,
            edited.option_c || templated.option_c,
            edited.option_d || templated.option_d
          ].join('\n')
        : [
            edited.question_content || templated.question_content,
            edited.solution || templated.solution
          ].join('\n');

      const response = await api.post('/review/templated/validate-latex', { content });
      setValidationResult(response.data);
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({
        is_valid: false,
        message: 'Validation failed',
        error_message: error.response?.data?.detail || 'Unknown error'
      });
    } finally {
      setValidating(false);
    }
  };

  // Save templated content
  const handleSaveTemplated = async (action) => {
    const templated = currentQuestion.templated;
    if (!templated) return;

    setSaving(true);
    try {
      if (action === 'agree') {
        await api.post(`/review/templated/${templated.type}/${currentQuestion.segment_id}/agree`);
      } else {
        const edited = getEditedValues();
        if (Object.keys(edited).length === 0) {
          showNotification('No changes to save', 'warning');
          return;
        }
        
        await api.post(`/review/templated/${templated.type}/${currentQuestion.segment_id}/override`, edited);
      }
      
      // Update local state
      setQuestions(prev => prev.map((q, idx) => 
        idx === currentIndex 
          ? { ...q, templated: { ...q.templated, is_verified_human: true } }
          : q
      ));
    } catch (error) {
      console.error('Save error:', error);
      showNotification('Failed to save: ' + (error.response?.data?.detail || 'Unknown error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Topic handlers
  const handleTopicAgree = async () => {
    const segment_id = currentQuestion.segment_id;
    setSaving(true);
    try {
      // Use new human review endpoint - null means accept AI topic
      await api.post(`/review/human/segments/${segment_id}/topic`, { topic_path: null });
      // Update local state
      setQuestions(prev => prev.map((q, idx) => 
        idx === currentIndex 
          ? { ...q, topic: { ...q.topic, is_verified: true } }
          : q
      ));
    } catch (error) {
      console.error('Topic agree error:', error);
      showNotification('Failed to save: ' + (error.response?.data?.detail || 'Unknown error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTopicOverrideOpen = () => {
    setOverrideTopic(currentQuestion.topic?.user_topic_path || currentQuestion.topic?.ai_topic_path || '');
    setTopicOverrideOpen(true);
  };

  const handleTopicOverrideSave = async () => {
    const segment_id = currentQuestion.segment_id;
    setSaving(true);
    try {
      // Use new human review endpoint - provide custom topic path
      await api.post(`/review/human/segments/${segment_id}/topic`, { topic_path: overrideTopic });
      // Update local state
      setQuestions(prev => prev.map((q, idx) => 
        idx === currentIndex 
          ? { ...q, topic: { ...q.topic, user_topic_path: overrideTopic, is_verified: true } }
          : q
      ));
      setTopicOverrideOpen(false);
    } catch (error) {
      console.error('Topic override error:', error);
      showNotification('Failed to save: ' + (error.response?.data?.detail || 'Unknown error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Answer handlers



  // Handle MCQ answer selection
  const handleMcqAnswerSelect = (option) => {
    const key = `segment_${currentQuestion.segment_id}`;
    setSelectedMcqAnswers(prev => ({
      ...prev,
      [key]: option
    }));
  };
  
  // Get selected MCQ answer for current question
  const getSelectedMcqAnswer = () => {
    const key = `segment_${currentQuestion.segment_id}`;
    return selectedMcqAnswers[key] || currentQuestion.answer?.ai_answer || currentQuestion.answer?.human_answer || '';
  };
  
  // Check if MCQ needs answer selection before locking
  const needsMcqAnswerSelection = () => {
    if (!currentQuestion.templated || currentQuestion.templated.type !== 'mcq') {
      return false;
    }
    if (!currentQuestion.answer || currentQuestion.answer.answer_type !== 'CANNOT_ANSWER') {
      return false;
    }
    return !getSelectedMcqAnswer();
  };

  // Helper to insert text at cursor position
  const insertAtCursor = (field, text) => {
    const currentValue = getEditedValues()[field] || currentQuestion.templated[field] || '';
    
    // Get the active textarea element
    const activeElement = document.activeElement;
    if (activeElement && activeElement.tagName === 'TEXTAREA') {
      const start = activeElement.selectionStart;
      const end = activeElement.selectionEnd;
      const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
      handleTemplatedEdit(field, newValue);
      
      // Set cursor position after the inserted command
      setTimeout(() => {
        activeElement.focus();
        activeElement.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    } else {
      // If no active textarea, append to the end
      handleTemplatedEdit(field, currentValue + (currentValue ? '\n' : '') + text);
    }
  };

  // Image upload handlers
  const handleImageUpload = async (file = null, insertField = null) => {
    const fileToUpload = file || imageFile;
    if (!fileToUpload) {
      showNotification('Please select an image', 'warning');
      return;
    }

    setUploadingImage(true);
    try {
      // Debug logging
      console.log('Uploading file:', {
        name: fileToUpload.name,
        type: fileToUpload.type,
        size: fileToUpload.size
      });
      
      const formData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);
      // Extract filename without extension for the filename field
      const filenameWithoutExt = fileToUpload.name.replace(/\.[^/.]+$/, '');
      formData.append('filename', filenameWithoutExt);

      const response = await api.post(
        `/database/documents/${documentId}/images/upload`,
        formData
        // Don't set Content-Type header - let browser set it with boundary
      );

      const result = response.data;
      
      // Copy includegraphics command to clipboard (only if document is focused)
      try {
        await navigator.clipboard.writeText(result.includegraphics_command);
      } catch (clipboardError) {
        console.warn('Could not copy to clipboard:', clipboardError);
      }
      
      // Update image map
      setImageMap(prev => ({
        ...prev,
        [result.filename]: result.id
      }));

      // Show appropriate message based on whether image already existed
      if (result.already_exists) {
        showNotification(
          `Image "${result.filename}" already exists. The LaTeX command has been copied to your clipboard.`,
          'info'
        );
      } else {
        showNotification(
          `Image "${result.filename}" uploaded successfully! The LaTeX command has been copied to your clipboard.`,
          'success'
        );
      }
      
      // Insert at cursor if field is specified
      if (insertField) {
        insertAtCursor(insertField, result.includegraphics_command);
      }
      
      // Reset form
      setImageUploadOpen(false);
      setImageFile(null);
      
      return result.includegraphics_command;
    } catch (error) {
      console.error('Image upload error:', error);
      console.error('Upload error details:', error.response?.data);
      showNotification(
        'Failed to upload image: ' + (error.response?.data?.detail || 'Unknown error'),
        'error'
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        showNotification('Please select a PNG or JPG file', 'warning');
        event.target.value = '';
        return;
      }
      
      setImageFile(file);
    }
  };
  
  // Handle drag and drop image upload
  const handleImageDrop = async (file, field) => {
    try {
      // Ensure the document has focus before upload
      document.body.focus();
      await handleImageUpload(file, field);
    } catch (error) {
      console.error('Error handling dropped image:', error);
    }
  };

  // Handle paste event for images
  const handlePaste = (e, field) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        console.log('Pasted file:', file, 'Type:', file?.type, 'Name:', file?.name);
        if (file) {
          // Validate file type
          const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
          const fileType = file.type || '';
          if (validTypes.includes(fileType)) {
            // Create a proper file object with a name if it doesn't have one
            let fileToUpload = file;
            if (!file.name || file.name === 'image.png' || file.name === 'image.jpeg') {
              const timestamp = new Date().getTime();
              const extension = fileType.includes('png') ? 'png' : 'jpg';
              // Read the file as blob first
              const reader = new FileReader();
              reader.onload = async function(e) {
                const arrayBuffer = e.target.result;
                const blob = new Blob([arrayBuffer], { type: fileType });
                const newFile = new File([blob], `pasted-image-${timestamp}.${extension}`, { 
                  type: fileType,
                  lastModified: Date.now()
                });
                console.log('Created new file:', newFile);
                handleImageUpload(newFile, field);
              };
              reader.readAsArrayBuffer(file);
              return; // Exit early, handleImageUpload will be called in the callback
            }
            handleImageUpload(fileToUpload, field);
          } else {
            showNotification('Please paste a PNG or JPEG image', 'warning');
          }
        }
      }
    }
  };



  // New unified review render function
  const renderUnifiedReview = () => {
    const { templated } = currentQuestion;
    
    // Handle unprocessed questions
    if (!templated) {
      return (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          px: 4,
          backgroundColor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: 2
        }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            This question has not been processed yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The AI has not yet generated content for this question. 
            Please wait for processing to complete before reviewing.
          </Typography>
        </Box>
      );
    }

    const edited = getEditedValues();
    
    return (
      <Box>
        {/* Stack of two horizontal grids */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: reviewThemeExtensions.layout.panelGap,
        }}>
          {/* Question Row - Preview and Editor side by side */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: reviewThemeExtensions.layout.panelGap,
            minHeight: '200px',
            alignItems: 'stretch', // Ensure equal heights
            '& > *': {
              minHeight: 0, // Allow children to shrink
              height: '100%', // Fill available height
            }
          }}>
            {/* Question Preview */}
            <QuestionPreview 
              templated={templated}
              edited={edited}
              documentId={documentId}
              imageMap={imageMap}
            />

            {/* Question Editor */}
            <DragDropLatexEditor 
              title="Question LaTeX"
              templated={templated}
              edited={edited}
              onEdit={handleTemplatedEdit}
              disabled={currentQuestion.verification_status?.is_fully_verified}
              modifierKey={getModifierKey()}
              showShortcut={!currentQuestion.verification_status?.is_fully_verified}
              onImageDrop={(file) => handleImageDrop(file, 'question_content')}
              onPaste={(e) => handlePaste(e, 'question_content')}
            />
          </Box>

          {/* Answer Row - Preview and Editor side by side */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: reviewThemeExtensions.layout.panelGap,
            minHeight: '200px',
            alignItems: 'stretch', // Ensure equal heights
            '& > *': {
              minHeight: 0, // Allow children to shrink
              height: '100%', // Fill available height
            }
          }}>
            {/* Answer Preview */}
            <AnswerPreview 
              templated={templated}
              edited={edited}
              currentQuestion={currentQuestion}
              documentId={documentId}
              imageMap={imageMap}
              selectedMcqAnswer={getSelectedMcqAnswer()}
              onMcqAnswerSelect={handleMcqAnswerSelect}
            />

            {/* Answer Editor - Now includes MCQ options */}
            <DragDropLatexEditor 
              title="Answer LaTeX"
              templated={templated}
              edited={edited}
              onEdit={handleTemplatedEdit}
              disabled={currentQuestion.verification_status?.is_fully_verified}
              modifierKey={getModifierKey()}
              showShortcut={!currentQuestion.verification_status?.is_fully_verified}
              onImageDrop={(file) => handleImageDrop(file, templated.type === 'mcq' ? 'option_a' : 'solution')}
              onPaste={(e) => handlePaste(e, templated.type === 'mcq' ? 'option_a' : 'solution')}
            />
          </Box>
        </Box>

        {/* Validation Result - Outside the grid */}
        <ValidationResult 
          validationResult={validationResult}
          compilationErrors={compilationErrors}
          onClose={() => {
            setValidationResult(null);
            setCompilationErrors([]);
          }}
        />

        {/* Action Buttons - Outside the grid */}
        <Box sx={{ mt: 3 }}>
          <ActionButtons
            isFullyVerified={currentQuestion.verification_status?.is_fully_verified}
            isLocking={false}
            needsMcqAnswer={needsMcqAnswerSelection()}
            validating={validating}
            onUploadImage={() => setImageUploadOpen(true)}
            onCompile={() => handleValidateSection(templated.type === 'extended' ? 'solution' : 'question')}
            onLock={handleLockIntoDatabase}
            onUnlock={handleUnlockFromDatabase}
            compileType={templated.type === 'extended' ? 'solution' : 'question'}
            modifierKey={getModifierKey()}
            onShowOriginal={() => setShowOriginalContent(!showOriginalContent)}
            showingOriginal={showOriginalContent}
            // Generate exam props
            onGenerateExam={handleGenerateExam}
            generatingExam={generatingExam}
            examGenerationTaskId={examGenerationTaskId}
            examGenerationStatus={examGenerationStatus}
            hasVerifiedQuestions={questions.some(q => q.verification_status?.is_fully_verified)}
            onRedownloadExam={handleRedownloadExam}
          />
        </Box>
        
        {/* Original Question Content - Collapsible */}
        {showOriginalContent && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: 1 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Original Question Content
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={reviewThemeExtensions.components.imageContainer}>
              {processLatexString(currentQuestion.content, parseInt(documentId), imageMap)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Removed old renderTemplatedReview function

  // Removed old renderLatexPreview function

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (questions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">No questions found for this document</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Check if all questions are unprocessed
  const hasProcessedQuestions = questions.some(q => !!q.templated);
  if (!hasProcessedQuestions) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Document Not Ready for Review
          </Typography>
          <Typography variant="body2">
            This document has not been processed by the AI yet. 
            Please wait for the AI to process the questions before reviewing.
          </Typography>
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back to Documents
        </Button>
      </Box>
    );
  }

  // Find next unverified question (skip unprocessed)
  const handleNextUnverified = () => {
    const nextUnverifiedIndex = questions.findIndex((q, idx) => 
      idx > currentIndex && !q.verification_status?.is_fully_verified && !!q.templated
    );
    
    if (nextUnverifiedIndex !== -1) {
      transitionToQuestion(nextUnverifiedIndex);
    } else {
      // Check if there are any unverified questions before current index
      const firstUnverifiedIndex = questions.findIndex((q, idx) => 
        !q.verification_status?.is_fully_verified && !!q.templated
      );
      
      if (firstUnverifiedIndex !== -1 && firstUnverifiedIndex < currentIndex) {
        showNotification('No more unverified questions ahead. Use Previous to go back.', 'info');
      } else {
        showNotification('All processed questions have been verified!', 'success');
      }
    }
  };

  // Removed unused hasNextUnverified function

  // Navigate to a specific question by segment ID
  const handleNavigateToQuestion = (segmentId) => {
    const targetIndex = questions.findIndex(q => q.segment_id === segmentId);
    if (targetIndex !== -1 && targetIndex !== currentIndex) {
      transitionToQuestion(targetIndex);
    }
  };


  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Notification System */}
      <NotificationSystem 
        notifications={notifications}
        onClose={removeNotification}
        onNavigateToQuestion={handleNavigateToQuestion}
        position="top-right"
      />
      
      {/* Left Sidebar - Question Navigation - Positioned absolutely */}
      <Box 
        sx={{ 
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: reviewThemeExtensions.layout.sidebarWidth, 
          borderRight: `1px solid ${theme.palette.divider}`,
          overflow: 'auto',
          bgcolor: 'background.paper',
          p: reviewThemeExtensions.spacing.md,
          zIndex: 1,
          transform: isLargeScreen ? 'translateX(0)' : 'translateX(-100%)',
          transition: reviewThemeExtensions.transitions.sidebar
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Questions</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {questions.map((q, idx) => {
            const isActive = idx === currentIndex;
            const isVerified = q.verification_status?.is_fully_verified;
            const hasFailed = failedSegments.has(q.segment_id);
            const isProcessed = !!q.templated;
            
            return (
              <Paper
                key={idx}
                variant={isActive ? "elevation" : "outlined"}
                sx={{
                  p: 2,
                  cursor: isProcessed ? 'pointer' : 'default',
                  position: 'relative',
                  opacity: isProcessed ? 1 : 0.5,
                  backgroundColor: isActive 
                    ? alpha(theme.palette.primary.main, 0.08) 
                    : hasFailed 
                      ? alpha(theme.palette.error.main, 0.08)
                      : !isProcessed
                        ? alpha(theme.palette.grey[300], 0.2)
                        : 'white',
                  border: isActive 
                    ? `2px solid ${theme.palette.primary.main}` 
                    : hasFailed
                      ? `2px solid ${theme.palette.error.main}`
                      : !isProcessed
                        ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                        : '1px solid rgba(0,0,0,0.12)',
                  '&:hover': isProcessed ? {
                    backgroundColor: alpha(
                      hasFailed ? theme.palette.error.main : theme.palette.primary.main, 
                      0.04
                    ),
                  } : {}
                }}
                onClick={() => {
                  if (idx !== currentIndex && isProcessed) {
                    transitionToQuestion(idx);
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      fontWeight={isActive ? 'bold' : 'normal'}
                      color={hasFailed ? 'error' : 'inherit'}
                    >
                      Q{q.question_number}{q.part_letter ? ` (${q.part_letter})` : ''}
                    </Typography>
                    <Typography variant="caption" color={hasFailed ? 'error' : 'textSecondary'}>
                      {!isProcessed ? 'Not processed' : `Segment ${q.segment_id}`}
                    </Typography>
                  </Box>
                  {!isProcessed && (
                    <WarningIcon 
                      color="disabled" 
                      sx={{ fontSize: 20 }}
                    />
                  )}
                  {isProcessed && isVerified && (
                    <CheckCircleIcon 
                      color="success" 
                      sx={{ fontSize: 20 }}
                    />
                  )}
                  {isProcessed && hasFailed && !isVerified && (
                    <ErrorIcon 
                      color="error" 
                      sx={{ fontSize: 20 }}
                    />
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
        
        {/* Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.grey[500], 0.1), borderRadius: 1 }}>
          <Typography variant="caption" display="block">
            Total: {questions.length}
          </Typography>
          <Typography variant="caption" display="block" color="success.main">
            Verified: {questions.filter(q => q.verification_status?.is_fully_verified).length}
          </Typography>
          <Typography variant="caption" display="block" color="warning.main">
            Unverified: {questions.filter(q => !q.verification_status?.is_fully_verified).length}
          </Typography>
        </Box>
      </Box>

      {/* Visual indicator when sidebar is hidden on small screens */}
      {!isLargeScreen && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            width: 8,
            height: 120,
            background: `linear-gradient(to right, ${alpha(theme.palette.info.main, 0.3)} 0%, ${alpha(theme.palette.info.main, 0.1)} 50%, transparent 100%)`,
            borderRadius: '0 4px 4px 0',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 2,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 2,
              height: 60,
              bgcolor: alpha(theme.palette.info.main, 0.4),
              borderRadius: 1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 6,
              height: 40,
              borderTop: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              borderRight: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              borderRadius: '0 3px 3px 0',
            }
          }}
        />
      )}

      {/* Main Content - Centered without considering sidebar */}
      <Box sx={{ width: '100%', overflow: 'auto' }}>
        <Box sx={{ maxWidth: 1900, mx: 'auto', p: 3 }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="textSecondary">
              Question {currentIndex + 1} of {questions.length}
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
          </Box>

          {/* Question info */}
          <Fade in={!isTransitioning} timeout={150}>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 2 }}>
              {/* Two column layout: Left (Question Info + Verification) | Right (Topic) */}
              <Grid container spacing={3} alignItems="center">
                {/* Left Column: Question Info + Verification Status */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {/* Question Info */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Question {currentQuestion.question_number}
                        {currentQuestion.part_letter && ` (${currentQuestion.part_letter})`}
                      </Typography>
                      <Chip 
                        label={`Segment ID: ${currentQuestion.segment_id}`} 
                        variant="outlined" 
                        size="small" 
                      />
                    </Box>
                    
                    {/* Verification Status */}
                    {currentQuestion.verification_status && (
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mb: 1 }}>
                          Verification Status
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            icon={currentQuestion.verification_status.is_topic_verified ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            label="Topic"
                            color={currentQuestion.verification_status.is_topic_verified ? 'success' : 'default'}
                            variant={currentQuestion.verification_status.is_topic_verified ? 'filled' : 'outlined'}
                            size="small"
                          />
                          <Chip
                            icon={currentQuestion.verification_status.is_answer_verified ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            label="Answer"
                            color={currentQuestion.verification_status.is_answer_verified ? 'success' : 'default'}
                            variant={currentQuestion.verification_status.is_answer_verified ? 'filled' : 'outlined'}
                            size="small"
                          />
                          <Chip
                            icon={currentQuestion.verification_status.is_template_verified ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            label="Template"
                            color={currentQuestion.verification_status.is_template_verified ? 'success' : 'default'}
                            variant={currentQuestion.verification_status.is_template_verified ? 'filled' : 'outlined'}
                            size="small"
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                {/* Right Column: Topic Selection */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mb: 1 }}>
                      Topic
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Tooltip 
                        title={!currentQuestion.verification_status?.is_fully_verified ? `Click to change topic (${getModifierKey()}+T)` : ""} 
                        placement="left"
                      >
                        <Chip
                          label={currentQuestion.topic?.user_topic_path || currentQuestion.topic?.ai_topic_path || 'Not categorized'}
                          color={currentQuestion.topic?.is_verified ? 'primary' : 'default'}
                          variant={currentQuestion.topic?.is_verified ? 'filled' : 'outlined'}
                          onClick={!currentQuestion.verification_status?.is_fully_verified ? handleTopicOverrideOpen : undefined}
                          sx={{
                            cursor: !currentQuestion.verification_status?.is_fully_verified ? 'pointer' : 'default',
                            maxWidth: '400px',
                            '& .MuiChip-label': {
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            },
                            fontSize: '0.875rem',
                            fontWeight: currentQuestion.topic?.is_verified ? 'bold' : 'normal',
                            '&:hover': !currentQuestion.verification_status?.is_fully_verified ? {
                              backgroundColor: currentQuestion.topic?.is_verified 
                                ? alpha(theme.palette.primary.main, 0.8)
                                : alpha(theme.palette.action.hover, 0.2)
                            } : {}
                          }}
                        />
                      </Tooltip>
                      {!currentQuestion.verification_status?.is_fully_verified && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleTopicAgree}
                          disabled={saving || currentQuestion.topic?.is_verified}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Accept
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
            </Card>
          </Fade>

          {/* Unified Review */}
          <Fade in={!isTransitioning} timeout={200}>
            <Paper sx={{ p: 3, mb: 3 }}>
              {renderUnifiedReview()}
            </Paper>
          </Fade>

          {/* Bottom Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handlePrevious}
              disabled={!questions.slice(0, currentIndex).some(q => !!q.templated)}
            >
              Previous
            </Button>
            
            {/* Show skip indicator if there are unprocessed questions between */}
            {(() => {
              const nextProcessedIdx = questions.findIndex((q, idx) => idx > currentIndex && !!q.templated);
              const hasUnprocessedBetween = nextProcessedIdx > currentIndex + 1;
              const prevProcessedIdx = [...questions].reverse().findIndex((q, idx) => 
                (questions.length - 1 - idx) < currentIndex && !!q.templated
              );
              const hasUnprocessedBefore = prevProcessedIdx !== -1 && 
                (questions.length - 1 - prevProcessedIdx) < currentIndex - 1;
              
              if (hasUnprocessedBetween || hasUnprocessedBefore) {
                return (
                  <Typography variant="caption" color="textSecondary" sx={{ mx: 2 }}>
                    <WarningIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                    Skipping unprocessed questions
                  </Typography>
                );
              }
              return null;
            })()}
            
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!questions.slice(currentIndex + 1).some(q => !!q.templated)}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Topic Override Dialog */}
      <Dialog open={topicOverrideOpen} onClose={() => setTopicOverrideOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Topic Classification</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              value={overrideTopic}
              onChange={(event, newValue) => setOverrideTopic(newValue || '')}
              options={topics}
              freeSolo
              renderInput={(params) => (
                <TextField {...params} label="Select or Enter Topic" fullWidth />
              )}
            />
            {currentQuestion.topic?.ai_topic_path && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                AI suggested: {currentQuestion.topic.ai_topic_path}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopicOverrideOpen(false)}>Cancel</Button>
          <Button onClick={handleTopicOverrideSave} variant="contained" disabled={saving}>
            Save Override
          </Button>
        </DialogActions>
      </Dialog>

      {/* Answer Override Dialog - removed (unused) */}

      {/* Image Upload Dialog */}
      <Dialog 
        open={imageUploadOpen} 
        onClose={() => !uploadingImage && setImageUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Image for Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                After uploading, the LaTeX command to include this image will be automatically copied to your clipboard.
              </Typography>
            </Alert>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select a PNG or JPG image to upload. The image will be linked to this document.
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <input
                accept="image/png,image/jpeg,image/jpg"
                style={{ display: 'none' }}
                id="image-file-upload"
                type="file"
                onChange={handleImageFileChange}
                disabled={uploadingImage}
              />
              <label htmlFor="image-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  disabled={uploadingImage}
                >
                  {imageFile ? `Selected: ${imageFile.name}` : 'Select Image File'}
                </Button>
              </label>
            </Box>

            {imageFile && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Ready to upload: <strong>{imageFile.name}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  The LaTeX command will be copied to your clipboard after upload.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageUploadOpen(false)} disabled={uploadingImage}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleImageUpload()} 
            variant="contained" 
            disabled={!imageFile || uploadingImage}
            startIcon={uploadingImage && <CircularProgress size={20} />}
          >
            {uploadingImage ? 'Uploading...' : 'Upload Image'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* In-App Notification */}
      <InAppNotification
        open={notification.open}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
        severity={notification.severity}
      />
    </Box>
  );
};

export default CompleteReview;
